import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import Navigation from '../../components/Navigation';
import Footer from '../../components/Footer';
import SEO from '../shared/SEO';
import { useTheme } from '../../hooks/useTheme';
import { generateOrganizationSchema, generateWebSiteSchema } from '../../lib/seo';

const { FiBookOpen, FiHeart, FiInstagram, FiFacebook, FiMessageCircle } = FiIcons;

function AboutPage() {
  const { theme } = useTheme();

  const schema = {
    '@context': 'https://schema.org',
    '@graph': [
      (() => {
        const { '@context': _, ...organization } = generateOrganizationSchema();
        return organization;
      })(),
      (() => {
        const { '@context': _, ...website } = generateWebSiteSchema();
        return website;
      })(),
      {
        '@type': 'AboutPage',
        '@id': 'https://omakase.tw/about#webpage',
        url: 'https://omakase.tw/about',
        name: '關於 Omakase TW',
        description: '認識 Omakase TW：以清楚、可靠且尊重文化脈絡的內容，分享 Omakase、日本料理、季節食材與用餐禮儀。',
        inLanguage: 'zh-TW',
        isPartOf: { '@id': 'https://omakase.tw/#website' },
        about: { '@id': 'https://omakase.tw/#organization' },
      },
    ],
  };

  return (
    <>
      <SEO
        title="關於 Omakase TW"
        description="認識 Omakase TW：以清楚、可靠且尊重文化脈絡的內容，分享 Omakase、日本料理、季節食材與用餐禮儀。"
        keywords="Omakase TW,關於我們,日本料理文化,Omakase指南,用餐禮儀"
        url="/about"
        type="website"
        schema={schema}
      />

      <div className={`min-h-screen ${theme === 'dark' ? 'dark' : ''}`}>
        <Navigation />

        <main className="pt-20">
          <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="text-center mb-14"
            >
              <h1 className="text-4xl md:text-5xl font-bold text-slate-900 dark:text-white mb-5">
                關於 Omakase TW
              </h1>
              <p className="text-xl text-slate-600 dark:text-slate-400 max-w-3xl mx-auto leading-relaxed">
                我們以清楚、可靠且尊重文化脈絡的內容，帶你認識 Omakase 與日本料理的深度。
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
              {[
                {
                  icon: FiBookOpen,
                  title: '知識整理',
                  description: '從初次預約、用餐禮儀到季節食材，將複雜資訊整理成容易理解的實用指南。',
                },
                {
                  icon: FiHeart,
                  title: '文化尊重',
                  description: '重視料理背後的職人精神、食材來源與文化語境，而不只停留在菜色介紹。',
                },
                {
                  icon: FiMessageCircle,
                  title: '持續交流',
                  description: '歡迎透過社群提出閱讀建議與主題想法，讓內容隨著讀者需求持續更新。',
                },
              ].map((item, index) => (
                <motion.section
                  key={item.title}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-7 shadow-sm"
                >
                  <SafeIcon icon={item.icon} className="w-8 h-8 text-orange-500 mb-5" />
                  <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-3">
                    {item.title}
                  </h2>
                  <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
                    {item.description}
                  </p>
                </motion.section>
              ))}
            </div>

            <section className="bg-gradient-to-br from-orange-50 to-rose-50 dark:from-slate-800 dark:to-slate-900 rounded-2xl p-8 md:p-10 text-center border border-orange-100 dark:border-slate-700">
              <h2 className="text-2xl md:text-3xl font-bold text-slate-900 dark:text-white mb-4">
                想與我們交流嗎？
              </h2>
              <p className="text-slate-600 dark:text-slate-400 max-w-2xl mx-auto mb-7 leading-relaxed">
                若有內容建議、主題想法或想分享你的 Omakase 體驗，歡迎透過以下社群平台與 Omakase TW 聯絡。
              </p>
              <div className="flex flex-wrap items-center justify-center gap-4">
                <a
                  href="https://www.instagram.com/omakase.tw"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-full bg-slate-900 dark:bg-white px-5 py-3 text-white dark:text-slate-900 font-medium transition-transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-orange-500"
                >
                  <SafeIcon icon={FiInstagram} className="w-5 h-5" />
                  Instagram
                </a>
                <a
                  href="https://www.facebook.com/omakasetw"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-full bg-white dark:bg-slate-800 px-5 py-3 text-slate-900 dark:text-white font-medium border border-slate-200 dark:border-slate-700 transition-transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-orange-500"
                >
                  <SafeIcon icon={FiFacebook} className="w-5 h-5" />
                  Facebook
                </a>
              </div>
            </section>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}

export default AboutPage;